import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';
import {
  Breadcrumb,
  BreadcrumbItem,
  Content,
  Icon,
  PageBreadcrumb,
  PageSection,
  PageSectionVariants,
  Tab,
  TabTitleText,
  Tabs,
} from '@patternfly/react-core';
import { CloudServerIcon } from '@patternfly/react-icons';
import Unauthenticated from '@app/Unauthenticated';
import { NotFound } from '@app/NotFound/NotFound';
import { useUserContext } from '../../../contexts/user/UserContext';
import {
  useAdminVerdaInstanceTypes,
  useAdminVerdaLocations,
  useAdminVerdaProductLinks,
} from '../../../hooks/useAdminVerda';
import VerdaBalanceTab from './VerdaBalanceTab';
import VerdaLocationsTab from './VerdaLocationsTab';
import VerdaInstanceTypesTab from './VerdaInstanceTypesTab';
import VerdaAvailabilityTab from './VerdaAvailabilityTab';
import VerdaAvailabilityHistoryTab from './VerdaAvailabilityHistoryTab';
import VerdaServersTab from './VerdaServersTab';
import VerdaFindingsTab from './VerdaFindingsTab';
import VerdaInvoicesTab from './VerdaInvoicesTab';
import VerdaCostLedgerTab from './VerdaCostLedgerTab';
import VerdaStorageProductsTab from './VerdaStorageProductsTab';

// One key per tab, in display order; the default (`balance`) carries no `?tab=`.
const VERDA_TAB_KEYS = [
  'balance',
  'locations',
  'instance-types',
  'availability',
  'availability-history',
  'servers',
  'findings',
  'invoices',
  'cost-ledger',
  'storage',
] as const;
type VerdaTabKey = (typeof VERDA_TAB_KEYS)[number];

const isVerdaTabKey = (value: unknown): value is VerdaTabKey =>
  typeof value === 'string' && (VERDA_TAB_KEYS as readonly string[]).includes(value);

/**
 * Admin › Verda — the Ancalagon-staff-only surface over our account with Verda, the GPU-cloud
 * provider we resell: Balance, Locations, Instance types and Availability (live Verda reads), Availability
 * history (the capacity series the sampler records every 5 minutes — a database read, the business-plan
 * report), then the resold GPU servers — GPU servers, Findings, GPU invoices and Cost ledger (database reads) — and the
 * Storage products that price supplementary storage in customer GPU budgets (a database read). Tabs are
 * mounted lazily (mountOnEnter) so a tab's calls only run once it is opened. Balance, Availability, the
 * four GPU tabs and Storage products own their reads; the locations and instance-types reads are lifted
 * here because Availability joins them too — each still starts only when the first tab needing it is opened, and is
 * then shared (one Verda call, one Refresh) instead of fetched once per tab. The catalog-product links
 * (which instance types already have an hourly product) follow the catalog the same way.
 *
 * The active tab is URL-backed (`?tab=servers`, …; none for Balance, replace so tab switches don't
 * pile up in history), so findings and the server detail breadcrumb can link to a tab.
 *
 * Gating is enforced in two independent places (defense in depth):
 *   1. AppLayout hides the whole "Admin" nav group unless `user.is_ancalagon_staff`.
 *   2. This page guards itself: a non-staff user who types /admin/verda directly gets <NotFound />
 *      — the same response as a non-existent route. Tabs defer their fetches on the same flag.
 *      The backend /admin routes enforce this authoritatively; this is the frontend mirror.
 */
export function Verda() {
  const auth = useAuth();
  const user = useUserContext();
  const isStaff = user?.is_ancalagon_staff === true;
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedTab = searchParams.get('tab');
  const activeTab: VerdaTabKey = isVerdaTabKey(requestedTab) ? requestedTab : 'balance';
  const [visited, setVisited] = useState<ReadonlySet<VerdaTabKey>>(() => new Set<VerdaTabKey>([activeTab]));
  // A tab reached through the URL (a findings link, the back button) counts as visited too.
  if (!visited.has(activeTab)) {
    setVisited(new Set(visited).add(activeTab));
  }
  const instanceTypes = useAdminVerdaInstanceTypes(
    isStaff && (visited.has('instance-types') || visited.has('availability')),
  );
  const locations = useAdminVerdaLocations(isStaff && (visited.has('locations') || visited.has('availability')));
  // The catalog-product links (a Manager-side read) ride along with the catalog they annotate.
  const productLinks = useAdminVerdaProductLinks(
    isStaff && (visited.has('instance-types') || visited.has('availability')),
  );

  const selectTab = (key: VerdaTabKey) => {
    const next = new URLSearchParams(searchParams);
    if (key === 'balance') {
      next.delete('tab');
    } else {
      next.set('tab', key);
    }
    // The invoice filter belongs to the GPU invoices tab (a findings link); leaving the tab drops it.
    if (key !== 'invoices') {
      next.delete('invoice');
    }
    setSearchParams(next, { replace: true });
    setVisited((prev) => (prev.has(key) ? prev : new Set(prev).add(key)));
  };

  if (!auth.isAuthenticated) {
    return <Unauthenticated />;
  }
  if (!isStaff) {
    return <NotFound />;
  }

  return (
    <>
      <PageBreadcrumb hasBodyWrapper={false} hasShadowBottom={false}>
        <Breadcrumb ouiaId="BasicBreadcrumb">
          <BreadcrumbItem>Admin</BreadcrumbItem>
          <BreadcrumbItem isActive>Verda</BreadcrumbItem>
        </Breadcrumb>
      </PageBreadcrumb>
      <PageSection hasBodyWrapper variant={PageSectionVariants.default} isWidthLimited hasShadowBottom={true}>
        <Content component="h1">
          <Icon size="lg" style={{ marginRight: '8px' }}>
            <CloudServerIcon />
          </Icon>
          Verda
        </Content>
        <Content component="p">
          Our account with Verda, the GPU-cloud provider we resell, and the GPU servers we sell on it.
        </Content>
      </PageSection>
      <PageSection hasBodyWrapper={false}>
        <Tabs
          activeKey={activeTab}
          onSelect={(_event, tabKey) => selectTab(tabKey as VerdaTabKey)}
          aria-label="Verda sections"
          mountOnEnter
        >
          <Tab eventKey="balance" title={<TabTitleText>Balance</TabTitleText>}>
            <div className="pf-v6-u-pt-md">
              <VerdaBalanceTab isStaff={isStaff} />
            </div>
          </Tab>
          <Tab eventKey="locations" title={<TabTitleText>Locations</TabTitleText>}>
            <div className="pf-v6-u-pt-md">
              <VerdaLocationsTab {...locations} />
            </div>
          </Tab>
          <Tab eventKey="instance-types" title={<TabTitleText>Instance types</TabTitleText>}>
            <div className="pf-v6-u-pt-md">
              <VerdaInstanceTypesTab {...instanceTypes} productLinks={productLinks} />
            </div>
          </Tab>
          <Tab eventKey="availability" title={<TabTitleText>Availability</TabTitleText>}>
            <div className="pf-v6-u-pt-md">
              <VerdaAvailabilityTab
                isStaff={isStaff}
                instanceTypes={instanceTypes}
                locations={locations}
                productLinks={productLinks}
              />
            </div>
          </Tab>
          <Tab eventKey="availability-history" title={<TabTitleText>Availability history</TabTitleText>}>
            <div className="pf-v6-u-pt-md">
              <VerdaAvailabilityHistoryTab isStaff={isStaff} />
            </div>
          </Tab>
          <Tab eventKey="servers" title={<TabTitleText>GPU servers</TabTitleText>}>
            <div className="pf-v6-u-pt-md">
              <VerdaServersTab isStaff={isStaff} />
            </div>
          </Tab>
          <Tab eventKey="findings" title={<TabTitleText>Findings</TabTitleText>}>
            <div className="pf-v6-u-pt-md">
              <VerdaFindingsTab isStaff={isStaff} />
            </div>
          </Tab>
          <Tab eventKey="invoices" title={<TabTitleText>GPU invoices</TabTitleText>}>
            <div className="pf-v6-u-pt-md">
              <VerdaInvoicesTab isStaff={isStaff} />
            </div>
          </Tab>
          <Tab eventKey="cost-ledger" title={<TabTitleText>Cost ledger</TabTitleText>}>
            <div className="pf-v6-u-pt-md">
              <VerdaCostLedgerTab isStaff={isStaff} />
            </div>
          </Tab>
          <Tab eventKey="storage" title={<TabTitleText>Storage products</TabTitleText>}>
            <div className="pf-v6-u-pt-md">
              <VerdaStorageProductsTab isStaff={isStaff} />
            </div>
          </Tab>
        </Tabs>
      </PageSection>
    </>
  );
}

export default Verda;
